import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import { FooterSite, HeaderSite, SeoHead } from "@acme/feature/landing";
import {
    capabilityLabel, compareSlug, formatContext, formatMonth, formatPrice,
    getActiveModelPages, getComparePairs, getModelPage, type ModelPageData,
} from "../../lib/model-pages";

type Props = {
    model: ModelPageData;
    siblings: ModelPageData[];
    compares: { slug: string; label: string }[];
};

export default function ModelPage({ model, siblings, compares }: Props) {
    const caps = model.capabilities.map(capabilityLabel).filter((c): c is string => !!c);
    const faq = [
        { question: `What is the context window of ${model.name}?`, answer: `${model.name} accepts up to ${model.contextWindow.toLocaleString('en-US')} tokens of context and can return up to ${model.maxOutputTokens.toLocaleString('en-US')} output tokens per reply.` },
        { question: `When was ${model.name} released?`, answer: model.releaseDate ? `${model.providerName} released ${model.name} in ${formatMonth(model.releaseDate)}.` : `${model.providerName} has not published a release date for ${model.name}.` },
        { question: `How do I use ${model.name}?`, answer: `Add your ${model.providerName} API key in DapperGPT, pick ${model.name} in the model picker, and start chatting. You pay ${model.providerName} directly for usage.` },
    ];
    const rows: [string, string][] = [
        ['Provider', model.providerName],
        ['API model ID', model.id],
        ['Released', formatMonth(model.releaseDate)],
        ['Context window', `${model.contextWindow.toLocaleString('en-US')} tokens`],
        ['Max output', `${model.maxOutputTokens.toLocaleString('en-US')} tokens`],
        ['Reasoning model', model.reasoning ? 'Yes' : 'No'],
        ['Price per 1M tokens (input / output)', formatPrice(model.pricing)],
        ...(model.knowledgeCutoff ? [['Knowledge cutoff', model.knowledgeCutoff] as [string, string]] : []),
    ];

    return (
        <>
            <SeoHead
                path={`/models/${model.id}`}
                title={`${model.name}: Context Window, Specs & Release Date | DapperGPT`}
                description={`${model.name} by ${model.providerName}: ${formatContext(model.contextWindow)} context, ${formatContext(model.maxOutputTokens)} max output, released ${formatMonth(model.releaseDate)}. ${model.description}. Use it with your own API key.`}
                faq={faq}
            />
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col text-gray-900 dark:text-gray-100">
                <HeaderSite containerClassName="!max-w-7xl px-6" />
                <main className="flex-grow container mx-auto max-w-4xl px-4 py-14">
                    <nav className="text-sm text-gray-500" aria-label="Breadcrumb">
                        <Link href="/models" className="hover:underline">Models</Link> <span aria-hidden>/</span> {model.providerName}
                    </nav>
                    <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight">{model.name}</h1>
                    <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">{model.description}.</p>
                    {caps.length > 0 && (
                        <ul className="mt-5 flex flex-wrap gap-2 list-none p-0">
                            {caps.map(c => <li key={c} className="list-none rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-xs font-medium">{c}</li>)}
                        </ul>
                    )}

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link href={`/chat?model=${encodeURIComponent(model.id)}`} rel="nofollow" className="rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700">Chat with {model.name}</Link>
                        <Link href="/models" className="rounded-lg border border-gray-300 dark:border-gray-700 px-6 py-3 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800">All models</Link>
                    </div>

                    <section className="mt-12">
                        <h2 className="text-xl font-semibold">Specifications</h2>
                        <table className="mt-4 w-full text-sm">
                            <tbody>
                                {rows.map(([k, v]) => (
                                    <tr key={k} className="border-t border-gray-100 dark:border-gray-800">
                                        <th scope="row" className="py-3 pr-4 text-left font-medium text-gray-500 dark:text-gray-400 w-1/2">{k}</th>
                                        <td className={`py-3 ${k === 'API model ID' ? 'font-mono text-xs' : 'tabular-nums'}`}>{v}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mt-3 text-xs text-gray-500">Specs as published by {model.providerName}. Prices are standard list prices before caching or batch discounts.</p>
                    </section>

                    {compares.length > 0 && (
                        <section className="mt-12">
                            <h2 className="text-xl font-semibold">Compare {model.name}</h2>
                            <ul className="mt-4 grid sm:grid-cols-2 gap-2 list-none p-0">
                                {compares.map(c => (
                                    <li key={c.slug} className="list-none"><Link href={`/compare/${c.slug}`} className="block rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm hover:border-blue-500">{c.label}</Link></li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {siblings.length > 0 && (
                        <section className="mt-12">
                            <h2 className="text-xl font-semibold">More {model.providerName} models</h2>
                            <ul className="mt-4 flex flex-wrap gap-2 list-none p-0">
                                {siblings.map(s => (
                                    <li key={s.id} className="list-none"><Link href={`/models/${s.id}`} className="block rounded-full border border-gray-200 dark:border-gray-800 px-3 py-1 text-sm hover:border-blue-500">{s.name}</Link></li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section className="mt-12">
                        <h2 className="text-xl font-semibold">FAQ</h2>
                        <dl className="mt-4 space-y-5">
                            {faq.map(f => (
                                <div key={f.question}>
                                    <dt className="font-medium">{f.question}</dt>
                                    <dd className="mt-1 text-gray-600 dark:text-gray-400">{f.answer}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                </main>
                <FooterSite />
            </div>
        </>
    );
}

export const getStaticPaths: GetStaticPaths = () => ({
    paths: getActiveModelPages().map(m => ({ params: { id: m.id } })),
    fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = ({ params }) => {
    const model = getModelPage(String(params?.id));
    if (!model) return { notFound: true };
    const siblings = getActiveModelPages().filter(m => m.provider === model.provider && m.id !== model.id).slice(0, 8);
    const compares = getComparePairs()
        .filter(([a, b]) => a === model.id || b === model.id)
        .map(([a, b]) => ({ slug: compareSlug(a, b), label: `${getModelPage(a)!.name} vs ${getModelPage(b)!.name}` }));
    return { props: { model, siblings, compares } };
};
